// Reads the claim's currency for the Request FGL modal's Claim Amount label (IDH-1285).
//
// The source is the Claim Data Replica, handed to the page through router state — eventually
// consistent and explicitly not a live read of the claim service (CONTEXT.md — "Claim Data
// Replica"). A currency the replica has not caught up on renders as no prefix at all, never as an
// assumed IDR: an unlabelled figure beats one mislabelled for a provider billing in another
// currency.
//
// Tolerant of shape on purpose. The replica reaches this modal through several list pages, and a
// currency that silently vanishes because a caller nested the payload one level differently is
// indistinguishable on screen from a claim that genuinely has no currency yet. Only a claimData we
// hold but cannot read is a contract breach, and that one is reported rather than swallowed.

/** `claimData` as the replica may present it: the raw JSON column, or already-parsed. */
type ClaimData = string | Record<string, unknown> | null | undefined

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * The `claimData` payload, from any of the nestings the FGL callers pass.
 *
 * Both current callers pass router state (`{claimDetails: {claim: {...}}}`), but the inner
 * `{claim: {...}}` and a bare claim are accepted so a future caller cannot silently blank the label.
 */
const findClaimData = (source: unknown): ClaimData => {
  if (!isRecord(source)) return undefined

  const candidates = [
    (source.claimDetails as Record<string, unknown>)?.claim,
    source.claim,
    source,
  ]

  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue
    const claimData = candidate.claimData ?? candidate.ClaimData
    if (claimData) return claimData as ClaimData
  }

  return undefined
}

/** The replica writes PascalCase keys, but a camelCased projection must not blank the label. */
const readCurrencyKey = (parsed: unknown): string | undefined => {
  if (!isRecord(parsed)) return undefined

  const currency = parsed.Currency ?? parsed.currency
  if (typeof currency !== 'string') return undefined

  return currency.trim() || undefined
}

/**
 * The claim's currency code, or undefined when the replica does not carry one.
 *
 * Never throws — the modal must open for a provider with a patient waiting to be discharged even if
 * the replica hands us something unreadable.
 */
export const readClaimCurrency = (claimDetails: unknown): string | undefined => {
  const claimData = findClaimData(claimDetails)
  if (!claimData) return undefined

  if (typeof claimData !== 'string') return readCurrencyKey(claimData)

  try {
    return readCurrencyKey(JSON.parse(claimData))
  } catch {
    // Holding a claimData we cannot parse breaks the replica's contract. Surfacing it separates
    // "this claim has no currency" from "we could not read the one it has" — the ambiguity that
    // makes a missing prefix undiagnosable from the screen alone.
    console.warn('Request FGL: claimData is present but not parseable; Claim Amount left unlabelled')
    return undefined
  }
}
